import { AmicusBrief } from '@/types/AmicusBriefs'
import { Column, Table } from '@tanstack/solid-table'
import { flattenDeep } from 'lodash'
import {
  Component,
  createEffect,
  createMemo,
  createSignal,
  For,
  JSX,
  mergeProps,
  Show,
  splitProps,
} from 'solid-js'
import { ColumnTypes } from './AmicusBriefsTable'

type FilterProps = {
  column: Column<AmicusBrief>
  table: Table<AmicusBrief>
  class?: string
}

export const Filter: Component<FilterProps> = (props) => {
  const firstValue = createMemo(() =>
    props.table.getPreFilteredRowModel().flatRows[0]?.getValue(props.column.id)
  )

  const columnFilterValue = createMemo(() => props.column.getFilterValue())

  const tags = createMemo(() => [
    ...new Set([...flattenDeep(Array.from(props.column.getFacetedUniqueValues().keys()))]),
  ])
  return (
    <>
      {typeof firstValue() === 'number' ? (
        <div class={props.class}>
          <div class="grid grid-cols-2 gap-x-8">
            <DebouncedInput
              type="number"
              min={Number(props.column.getFacetedMinMaxValues()?.[0] ?? '')}
              max={Number(props.column.getFacetedMinMaxValues()?.[1] ?? '')}
              value={(columnFilterValue() as [number, number])?.[0] ?? ''}
              onInput={(value) =>
                props.column.setFilterValue((old: [number, number]) => [value, old?.[1]])
              }
              placeholder={`Min ${
                props.column.getFacetedMinMaxValues()?.[0]
                  ? `(${props.column.getFacetedMinMaxValues()?.[0]})`
                  : ''
              }`}
              class=""
            />
            <DebouncedInput
              type="number"
              min={Number(props.column.getFacetedMinMaxValues()?.[0] ?? '')}
              max={Number(props.column.getFacetedMinMaxValues()?.[1] ?? '')}
              value={(columnFilterValue() as [number, number])?.[1] ?? ''}
              onInput={(value) =>
                props.column.setFilterValue((old: [number, number]) => [old?.[0], value])
              }
              placeholder={`Max ${
                props.column.getFacetedMinMaxValues()?.[1]
                  ? `(${props.column.getFacetedMinMaxValues()?.[1]})`
                  : ''
              }`}
              class=""
            />
          </div>
          <div class="h-1" />
        </div>
      ) : (
        <div class={props.class}>
          <Show when={props.column.id === 'requestedBrief'}>
            <datalist id={props.column.id + 'list'}>
              <For each={tags().slice(0, 5000)}>{(value) => <option value={value} />}</For>
            </datalist>
          </Show>
          <DebouncedInput
            type="text"
            value={(columnFilterValue() ?? '') as string}
            onInput={(value) => {
              props.column.setFilterValue(value)
            }}
            columnType={props.column.columnDef.meta?.type}
            placeholder={`Search... (${
              props.column.id === 'keywords'
                ? tags().length
                : props.column.getFacetedUniqueValues().size
            })`}
            class="w-full"
            list={props.column.id + 'list'}
          />
          <div class="h-1" />
        </div>
      )}
    </>
  )
}

type DebouncedInputProps = {
  value: string | number
  // eslint-disable-next-line no-unused-vars
  onInput: (value: string | number | string[]) => void
  debounce?: number
  columnType?: ColumnTypes
} & Pick<JSX.IntrinsicElements['input'], 'type' | 'placeholder' | 'class' | 'min' | 'max' | 'list'>

const DebouncedInput: Component<DebouncedInputProps> = (props) => {
  const merged = mergeProps({ debounce: 500 }, props)
  const [local, others] = splitProps(merged, ['value', 'onInput', 'debounce', 'class'])
  const [inputValue, setInputValue] = createSignal<string | number>('')
  // Sync with prop value changes
  createEffect(() => setInputValue(local.value))

  // Handle debounced updates
  let timeout: NodeJS.Timeout
  createEffect(() => {
    clearTimeout(timeout)
    const currentValue = inputValue()

    timeout = setTimeout(() => {
      let processedValue: string | number | string[] = currentValue
      if (props.columnType === 'tags' && typeof currentValue === 'string') {
        processedValue = currentValue ? currentValue.split(',').filter((e) => e !== '') : ''
      }
      local.onInput(processedValue)
    }, local.debounce)

    return () => clearTimeout(timeout)
  })

  return (
    <input
      {...others}
      class={`focus:ring-rebrand-blue h-40 rounded-none text-[0.75rem] focus:border-blue ${local.class}`}
      value={inputValue()}
      onInput={(e) => setInputValue(e.currentTarget.value)}
    />
  )
}
